import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { ArrowsUpIcon, BankCardIcon } from "@/shared/icons";
import { Button, TabsNavigation } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import HeaderTitle from "@/shared/widgets/HeaderTitle";
import { ImprovementsTab } from "@/domains/estate/constants/improvementsTab";
import {
  improvementsSelectedTabSelector,
  improvementsTabsSelector,
  setSelectedImprovementsTab,
} from "@/domains/estate/slice";
import { getIsActiveSubscribe } from "@/domains/estate/helpers/getIsActiveSubscribe";
import { improvementsIconsList } from "@/domains/estate/helpers/improvementsIconsList";
import useImprovements from "@/domains/estate/hooks/useImprovements";
import EstateImprovementLevels from "@/domains/estate/components/improvements/EstateImprovementLevels";
import EstateImprovementStatus from "@/domains/estate/components/improvements/EstateImprovementStatus";
import EstateImprovementWrapper from "@/domains/estate/components/improvements/EstateImprovementWrapper";
import EstateImprovementsItem from "@/domains/estate/components/improvements/EstateImprovementsItem";

export default function HomeMenuImprovementsContainer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("estate");
  const dispatch = useAppDispatch();

  const improvementsTabs = useAppSelector(improvementsTabsSelector);
  const selectedImprovementsTab = useAppSelector(improvementsSelectedTabSelector);

  const {
    loading,
    improvements,
    subscriptions,
    setSelectedImprovementType,
    selectedImprovement,
    setSelectedSubscriptionType,
    selectedSubscription,
    pending,
    buy,
    isStartLoading,
    isEndLoading,
    loadSubscribeStart,
    loadSubscribeEnd,
  } = useImprovements();
  const isSubscribe = !!selectedSubscription?.type;

  const improvementIcon =
    improvementsIconsList[selectedImprovement?.type?.toLowerCase()] ||
    improvementsIconsList[selectedSubscription?.type?.toLowerCase()] ||
    ArrowsUpIcon;

  let headerTitle = "";
  if (selectedImprovement)
    headerTitle = t(`estate.improvements.types.${selectedImprovement.type}.name`);
  if (selectedSubscription)
    headerTitle = t(`estate.improvements.types.${selectedSubscription.type}.name`);

  const isActiveSubscribe = getIsActiveSubscribe(
    selectedSubscription?.transition,
    selectedSubscription?.status,
  );

  const onBack = () => {
    setSelectedImprovementType(null);
    setSelectedSubscriptionType(null);
  };

  const changeCurrentTab = useCallback(
    (tab: ImprovementsTab) => dispatch(setSelectedImprovementsTab(tab)),
    [dispatch],
  );

  const header = (
    <HeaderTitle
      title={t("estate.improvements.title")}
      isOpen={!!selectedImprovement || !!selectedSubscription}
      titleOpen={headerTitle}
      onBack={onBack}
    />
  );

  const price = selectedImprovement?.nextValuePrice?.amount ?? selectedSubscription?.price;

  const footer =
    // eslint-disable-next-line no-nested-ternary
    selectedImprovement && selectedImprovement.nextValuePrice ? (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <p className="text-20 font-bold text-accent-solid">
            {formatPrice(selectedImprovement.nextValuePrice.amount)} ₽
          </p>
          <p className="text-15 font-medium text-white/50">
            {isSubscribe
              ? t("estate.improvements.subscribeCost")
              : t("estate.improvements.improvementCost")}
          </p>
        </div>
        <Button
          className="gap-2.5 h-11"
          disabled={pending}
          loading={pending}
          onClick={() => {
            buy();
          }}
        >
          <BankCardIcon size={18} />
          {t("estate.improvements.buyUpgrade")}
        </Button>
      </div>
    ) : selectedSubscription ? (
      <div className="flex flex-col gap-5">
        {!isActiveSubscribe && (
          <div className="flex flex-col gap-1.5">
            <p className="text-20 font-bold text-accent-solid">{formatPrice(price)} ₽</p>
            <p className="text-15 font-medium text-white/50">
              {t(
                selectedImprovement
                  ? "estate.improvements.improvementCost"
                  : "estate.improvements.subscribeCost",
              )}
            </p>
          </div>
        )}
        <Button
          className="gap-2.5 h-11"
          disabled={isStartLoading || isEndLoading}
          loading={isStartLoading || isEndLoading}
          onClick={() =>
            isActiveSubscribe
              ? loadSubscribeEnd(selectedSubscription?.type)
              : loadSubscribeStart(selectedSubscription?.type)
          }
        >
          {!isActiveSubscribe && <BankCardIcon size={18} />}
          {t(
            isActiveSubscribe
              ? "estate.improvements.deleteSubscribe"
              : "estate.improvements.subscribe",
          )}
        </Button>
      </div>
    ) : null;

  return (
    <DeviceTabContentWrapper
      header={header}
      onClose={() => onClose()}
      footer={footer}
      className="p-0"
    >
      {!selectedImprovement && !selectedSubscription && (
        <>
          {loading && <Loader />}
          <TabsNavigation
            tabs={improvementsTabs}
            selectedTab={selectedImprovementsTab}
            i18fileName="estate"
            i18Path="estate.improvements.tab"
            onClick={changeCurrentTab}
          />
          <div className="flex flex-wrap gap-1.5 pt-2.5">
            {selectedImprovementsTab !== ImprovementsTab.Improvements &&
              subscriptions.map((improvement) => (
                <EstateImprovementsItem
                  key={improvement.type}
                  type={improvement.type}
                  isActive={getIsActiveSubscribe(improvement?.transition, improvement?.status)}
                  icon={improvementsIconsList[improvement?.type?.toLowerCase()] || ArrowsUpIcon}
                  onClick={() => setSelectedSubscriptionType(improvement.type)}
                  isSubscription
                />
              ))}
            {selectedImprovementsTab !== ImprovementsTab.Subscriptions &&
              improvements.map((improvement) => (
                <EstateImprovementsItem
                  key={improvement.type}
                  type={improvement.type}
                  icon={improvementsIconsList[improvement?.type?.toLowerCase()] || ArrowsUpIcon}
                  isBought={improvement.currentValue === improvement.maxValue}
                  level={improvement.maxValue > 1 ? improvement.currentValue + 1 : 0}
                  onClick={() => setSelectedImprovementType(improvement.type)}
                />
              ))}
          </div>
        </>
      )}
      {(selectedImprovement || selectedSubscription) && (
        <EstateImprovementWrapper
          type={selectedImprovement ? selectedImprovement?.type : selectedSubscription?.type}
          icon={improvementIcon}
        >
          <div className="pb-5">
            <div className="flex justify-between items-center pt-5 pb-3.5 px-1 border-b border-b-[#191919]">
              <p className="text-16 font-medium text-white/50">{t("estate.improvements.type")}:</p>
              <p className="text-16 font-medium text-white">
                {isSubscribe
                  ? t("estate.improvements.typesList.subscribe")
                  : t("estate.improvements.typesList.only")}
              </p>
            </div>
            {selectedImprovement?.maxValue > 1 ? (
              <div className="flex justify-between items-center py-3.5 px-1 border-b border-b-[#191919]">
                <p className="text-16 font-medium text-white/50">
                  {t("estate.improvements.currentLevel")}:
                </p>
                <p className="text-16 font-medium text-white">
                  {selectedImprovement?.currentValue && selectedImprovement.currentValue + 1}
                </p>
              </div>
            ) : (
              <EstateImprovementStatus
                isSubscribe={isSubscribe}
                isBought={selectedImprovement?.currentValue > 0}
                isActive={isActiveSubscribe}
              />
            )}
          </div>
          {selectedImprovement?.maxValue > 1 && (
            <EstateImprovementLevels
              type={selectedImprovement?.type}
              levels={selectedImprovement?.prices}
              currentLevelValue={selectedImprovement?.currentValue}
            />
          )}
        </EstateImprovementWrapper>
      )}
    </DeviceTabContentWrapper>
  );
}
